/*
  Wurst Burger & Chili Co. — Live Truck Bar engine
  ------------------------------------------------
  - Determines LIVE vs CLOSED from the visitor's device clock
  - Overnight shifts handled automatically (end <= start rolls to next day)
  - Ticking countdown to the next stop when it is under an hour away
  - Opt-in proximity ("you are about 4.2 mi away")
  - Add to Calendar via generated .ics (works offline, no library)
  - Optional published-Google-Sheet override, cached, fails back silently
*/
(function () {
  'use strict';

  var MIN_PER_DAY = 1440;
  var WEEK_MIN = MIN_PER_DAY * 7;
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var CACHE_KEY = 'wbScheduleCache';
  var CACHE_MS = 10 * 60 * 1000;

  /* ---------------------------------------------------------- time utils */

  /* Accepts "16:00", "4:00 PM", "4 PM", "4:00PM", "2:00 AM". Google Sheets
     formats time cells as 12-hour by default, so both must work. */
  function parseClock(value) {
    if (value === 0) { return 0; }
    if (!value) { return null; }
    var raw = String(value).trim().toUpperCase();
    var isPm = raw.indexOf('PM') > -1;
    var isAm = raw.indexOf('AM') > -1;
    raw = raw.replace(/[AP]M/g, '').trim();
    var parts = raw.split(':');
    var h = parseInt(parts[0], 10);
    var m = parts.length > 1 ? parseInt(parts[1], 10) : 0;
    if (isNaN(h)) { return null; }
    if (isNaN(m)) { m = 0; }
    if (isPm && h < 12) { h += 12; }
    if (isAm && h === 12) { h = 0; }
    if (h > 23) { h = h % 24; }
    return (h * 60) + m;
  }

  function formatClock(totalMinutes) {
    var mins = ((totalMinutes % MIN_PER_DAY) + MIN_PER_DAY) % MIN_PER_DAY;
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    var suffix = h >= 12 ? 'PM' : 'AM';
    var display = h % 12;
    if (display === 0) { display = 12; }
    return display + ':' + (m < 10 ? '0' + m : m) + ' ' + suffix;
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function nowWeekMinutes(now) {
    return (now.getDay() * MIN_PER_DAY) + (now.getHours() * 60) + now.getMinutes();
  }

  function buildWindows(slots) {
    var windows = [];
    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];
      var day = parseInt(slot.day, 10);
      var start = parseClock(slot.start);
      var end = parseClock(slot.end);
      if (isNaN(day) || start === null || end === null) { continue; }

      var absStart = (day * MIN_PER_DAY) + start;
      var absEnd = (day * MIN_PER_DAY) + end;
      if (absEnd <= absStart) { absEnd += MIN_PER_DAY; }

      windows.push({
        index: i,
        name: slot.name || 'Our next stop',
        address: slot.address || '',
        display: slot.display || '',
        lat: parseFloat(slot.lat),
        lng: parseFloat(slot.lng),
        day: day,
        start: absStart,
        end: absEnd,
        startClock: start,
        endClock: end
      });
    }
    windows.sort(function (a, b) { return a.start - b.start; });
    return windows;
  }

  function findActive(windows, current, graceMinutes) {
    var offsets = [0, -WEEK_MIN, WEEK_MIN];
    for (var i = 0; i < windows.length; i++) {
      var w = windows[i];
      for (var o = 0; o < offsets.length; o++) {
        var s = w.start + offsets[o];
        var e = w.end + offsets[o] + graceMinutes;
        if (current >= s && current < e) { return w; }
      }
    }
    return null;
  }

  function findNext(windows, current) {
    var best = null, bestDelta = Infinity;
    var offsets = [0, WEEK_MIN];
    for (var i = 0; i < windows.length; i++) {
      for (var o = 0; o < offsets.length; o++) {
        var delta = (windows[i].start + offsets[o]) - current;
        if (delta > 0 && delta < bestDelta) { bestDelta = delta; best = windows[i]; }
      }
    }
    if (!best) { return null; }
    return { slot: best, minutesAway: bestDelta };
  }

  function directionsUrl(slot) {
    var query = (!isNaN(slot.lat) && !isNaN(slot.lng))
      ? (slot.lat + ',' + slot.lng)
      : (slot.address || slot.name);
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(query);
  }

  function windowLabel(slot) {
    if (slot.display) { return slot.display; }
    return formatClock(slot.startClock) + ' - ' + formatClock(slot.endClock);
  }

  /* ------------------------------------------------------------ distance */

  function haversineMiles(lat1, lon1, lat2, lon2) {
    var R = 3958.8;
    var toRad = Math.PI / 180;
    var dLat = (lat2 - lat1) * toRad;
    var dLon = (lon2 - lon1) * toRad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /* ----------------------------------------------------------------- ICS */

  function nextDateForDay(dayIndex, minutes) {
    var now = new Date();
    var d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var delta = (dayIndex - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + delta);
    d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    if (d.getTime() < now.getTime()) { d.setDate(d.getDate() + 7); }
    return d;
  }

  function icsStamp(date) {
    return date.getUTCFullYear() + pad(date.getUTCMonth() + 1) + pad(date.getUTCDate())
      + 'T' + pad(date.getUTCHours()) + pad(date.getUTCMinutes()) + '00Z';
  }

  function icsEscape(text) {
    return String(text || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  }

  function downloadIcs(slot, shopName) {
    var startDate = nextDateForDay(slot.day, slot.startClock);
    var durationMin = slot.end - slot.start;
    var endDate = new Date(startDate.getTime() + durationMin * 60000);
    var uid = 'wb-' + slot.day + '-' + slot.startClock + '-' + Date.now() + '@wurstburgerco.com';

    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Wurst Burger and Chili Co//Truck Schedule//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:' + uid,
      'DTSTAMP:' + icsStamp(new Date()),
      'DTSTART:' + icsStamp(startDate),
      'DTEND:' + icsStamp(endDate),
      'RRULE:FREQ=WEEKLY',
      'SUMMARY:' + icsEscape(shopName + ' at ' + slot.name),
      'DESCRIPTION:' + icsEscape('Smoked burgers, chili dogs and loaded sides. ' + windowLabel(slot) + '.'),
      'LOCATION:' + icsEscape(slot.address || slot.name),
      'BEGIN:VALARM',
      'TRIGGER:-PT30M',
      'ACTION:DISPLAY',
      'DESCRIPTION:' + icsEscape(shopName + ' is about to open at ' + slot.name),
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ];

    var blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'wurst-burger-' + slot.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 1500);
  }

  /* ------------------------------------------------- optional sheet feed */

  function parseCsv(text) {
    var rows = [], row = [], field = '', inQuotes = false;
    for (var i = 0; i < text.length; i++) {
      var ch = text.charAt(i);
      if (inQuotes) {
        if (ch === '"') {
          if (text.charAt(i + 1) === '"') { field += '"'; i++; }
          else { inQuotes = false; }
        } else { field += ch; }
      } else if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { row.push(field); field = ''; }
      else if (ch === '\n') { row.push(field); field = ''; rows.push(row); row = []; }
      else if (ch !== '\r') { field += ch; }
    }
    row.push(field);
    if (row.length > 1 || row[0] !== '') { rows.push(row); }
    return rows;
  }

  var DAY_LOOKUP = {
    'sun': 0, 'sunday': 0, 'mon': 1, 'monday': 1, 'tue': 2, 'tues': 2, 'tuesday': 2,
    'wed': 3, 'weds': 3, 'wednesday': 3, 'thu': 4, 'thur': 4, 'thurs': 4, 'thursday': 4,
    'fri': 5, 'friday': 5, 'sat': 6, 'saturday': 6
  };

  function normalizeDay(value) {
    var raw = String(value || '').trim().toLowerCase();
    if (raw === '') { return null; }
    if (DAY_LOOKUP.hasOwnProperty(raw)) { return DAY_LOOKUP[raw]; }
    var n = parseInt(raw, 10);
    if (!isNaN(n) && n >= 0 && n <= 6) { return n; }
    return null;
  }

  function rowsToSlots(rows) {
    if (!rows.length) { return []; }
    var header = rows[0].map(function (h) { return String(h).trim().toLowerCase(); });
    function col(n) { return header.indexOf(n); }
    var iDay = col('day'), iStart = col('start'), iEnd = col('end');
    var iName = col('name'), iAddr = col('address'), iDisp = col('display');
    var iLat = col('lat'); if (iLat === -1) { iLat = col('latitude'); }
    var iLng = col('lng'); if (iLng === -1) { iLng = col('longitude'); }
    if (iName === -1) { iName = col('location'); }
    if (iName === -1) { iName = col('stop'); }
    if (iDisp === -1) { iDisp = col('time'); }
    if (iDisp === -1) { iDisp = col('hours'); }
    if (iDay === -1 || iStart === -1 || iName === -1) { return []; }

    var slots = [];
    for (var r = 1; r < rows.length; r++) {
      var c = rows[r];
      if (!c || c.length === 1) { continue; }
      var day = normalizeDay(c[iDay]);
      var name = String(c[iName] || '').trim();
      if (day === null || name === '') { continue; }
      slots.push({
        day: day,
        start: String(c[iStart] || '').trim(),
        end: iEnd > -1 ? String(c[iEnd] || '').trim() : '',
        name: name,
        address: iAddr > -1 ? String(c[iAddr] || '').trim() : '',
        display: iDisp > -1 ? String(c[iDisp] || '').trim() : '',
        lat: iLat > -1 ? String(c[iLat] || '').trim() : '',
        lng: iLng > -1 ? String(c[iLng] || '').trim() : ''
      });
    }
    return slots;
  }

  /* Accepts the plain /edit share link, a /pub?output=csv link, or a gviz
     link, and normalizes to something the browser can actually fetch. */
  function normalizeFeedUrl(url) {
    if (!url) { return ''; }
    var u = String(url).trim();
    if (u.indexOf('output=csv') > -1 || u.indexOf('tqx=out:csv') > -1) { return u; }
    if (u.indexOf('/pub') > -1) {
      return u + (u.indexOf('?') > -1 ? '&' : '?') + 'output=csv';
    }
    var m = u.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (m) {
      var gid = u.match(/[#&?]gid=([0-9]+)/);
      return 'https://docs.google.com/spreadsheets/d/' + m[1] + '/gviz/tq?tqx=out:csv'
             + (gid ? '&gid=' + gid[1] : '');
    }
    return u;
  }

  function fetchFeed(rawUrl, done) {
    var url = normalizeFeedUrl(rawUrl);
    if (!url) { done(null); return; }
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var obj = JSON.parse(raw);
        if (obj.url === url && (Date.now() - obj.at) < CACHE_MS) { done(obj.slots); return; }
      }
    } catch (e) {}
    if (!window.fetch) { done(null); return; }
    fetch(url, { credentials: 'omit' })
      .then(function (res) { if (!res.ok) { throw new Error('bad status'); } return res.text(); })
      .then(function (text) {
        var slots = rowsToSlots(parseCsv(text));
        if (!slots.length) { done(null); return; }
        try { window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ url: url, at: Date.now(), slots: slots })); } catch (e) {}
        done(slots);
      })
      .catch(function () { done(null); });
  }

  /* ---------------------------------------------------------------- init */

  function init(root) {
    if (root.getAttribute('data-wb-ready') === '1') { return; }
    root.setAttribute('data-wb-ready', '1');

    var dataEl = root.querySelector('[data-wb-schedule]');
    var slots = [];
    if (dataEl) {
      try { slots = JSON.parse(dataEl.textContent || dataEl.innerHTML || '[]'); } catch (e) { slots = []; }
    }

    var grace = parseInt(root.getAttribute('data-grace') || '15', 10);
    if (isNaN(grace)) { grace = 15; }
    var wantProximity = root.getAttribute('data-proximity') === '1';
    var wantCalendar = root.getAttribute('data-calendar') === '1';
    var shopName = 'Wurst Burger and Chili Co.';

    var statusEl = root.querySelector('[data-wb-status]');
    var subEl = root.querySelector('[data-wb-sub]');
    var directionsEl = root.querySelector('[data-wb-directions]');
    var modalSpotEl = root.querySelector('[data-wb-modal-spot]');
    var modalWindowEl = root.querySelector('[data-wb-modal-window]');
    var listEl = root.querySelector('[data-wb-list]');
    var nearEl = root.querySelector('[data-wb-near]');
    var nearBtn = root.querySelector('[data-wb-nearbtn]');

    var windows = buildWindows(slots);
    var currentTarget = null;
    var visitorPos = null;
    var ticker = null;
    var livePing = null;
    var orderingOff = false;

    var liveUrl = root.getAttribute('data-live');
    var liveStale = parseInt(root.getAttribute('data-live-stale') || '240', 10);
    if (liveUrl && window.wbLiveGps) {
      window.wbLiveGps(liveUrl, liveStale, function (ping) {
        if (ping) { livePing = ping; render(); }
      });
    }
    /* Read the global ordering switch directly (independent of GPS freshness).
       When ordering is paused, the bar drops the LIVE badge and says so. */
    if (liveUrl && window.fetch) {
      fetch(liveUrl, { credentials: 'omit' })
        .then(function (r) { return r.json(); })
        .then(function (st) {
          if (st && st.ordering === 'off') { orderingOff = true; render(); }
          else if (orderingOff) { orderingOff = false; render(); }
        })
        .catch(function () {});
    }

    /* Manual override short-circuits everything. */
    var overrideText = root.getAttribute('data-override');
    if (overrideText) {
      root.classList.remove('wb-bar--live');
      root.classList.add('wb-bar--closed');
      if (statusEl) { statusEl.textContent = overrideText; }
      if (subEl) { subEl.textContent = ''; }
      if (directionsEl) { directionsEl.style.display = 'none'; }
      if (nearBtn) { nearBtn.style.display = 'none'; }
      if (modalSpotEl) { modalSpotEl.textContent = overrideText; }
    } else {
      render();
      ticker = setInterval(render, 1000);

      var feedUrl = root.getAttribute('data-feed');
      if (feedUrl) {
        fetchFeed(feedUrl, function (feedSlots) {
          if (feedSlots && feedSlots.length) {
            windows = buildWindows(feedSlots);
            rebuildList(feedSlots);
            render();
          }
        });
      }
    }

    function rebuildList(feedSlots) {
      if (!listEl) { return; }
      var html = '';
      for (var i = 0; i < feedSlots.length; i++) {
        var s = feedSlots[i];
        var label = s.display || (s.start + ' - ' + s.end);
        html += '<li class="wb-sched__row" data-wb-row="' + String(s.name).replace(/"/g, '&quot;') + '|' + s.day + '">'
              + '<span class="wb-sched__day">' + DAY_SHORT[s.day] + '</span>'
              + '<span class="wb-sched__place">' + String(s.name).replace(/</g, '&lt;') + '</span>'
              + '<span class="wb-sched__time">' + String(label).replace(/</g, '&lt;') + '</span>'
              + (wantCalendar ? '<button type="button" class="wb-sched__ics" data-wb-ics="' + i + '" title="Add to calendar">+</button>' : '')
              + '</li>';
      }
      listEl.innerHTML = html;
    }

    function render() {
      if (!windows.length) {
        if (statusEl) { statusEl.textContent = 'Check our socials for today\u2019s stop'; }
        if (directionsEl) { directionsEl.style.display = 'none'; }
        return;
      }

      var now = new Date();
      var current = nowWeekMinutes(now);
      var active = findActive(windows, current, grace);

      /* A fresh GPS ping outranks the schedule: show where the truck IS. */
      if (livePing) {
        var liveName = livePing.label || (active ? active.name : 'On location');
        currentTarget = {
          name: liveName, address: '', display: '',
          lat: parseFloat(livePing.lat), lng: parseFloat(livePing.lng),
          day: now.getDay(), start: 0, end: 0,
          startClock: 0, endClock: 0
        };
        root.classList.remove('wb-bar--closed');
        if (orderingOff) {
          root.classList.remove('wb-bar--live');
          root.classList.add('wb-bar--paused');
          if (dotEl) { dotEl.classList.add('is-off'); }
          if (statusEl) { statusEl.textContent = 'AT ' + liveName + ' \u2014 online orders paused'; }
        } else {
          root.classList.add('wb-bar--live');
          root.classList.remove('wb-bar--paused');
          if (dotEl) { dotEl.classList.remove('is-off'); }
          if (statusEl) { statusEl.textContent = 'NOW SERVING \u2014 ' + liveName; }
        }
        if (subEl) { subEl.textContent = 'Live GPS \u00b7 pinned ' + livePing.ageMinutes + ' min ago'; }
        if (modalSpotEl) { modalSpotEl.textContent = liveName; }
        if (modalWindowEl) { modalWindowEl.textContent = 'Live position \u00b7 tap Directions'; }
        if (directionsEl) {
          directionsEl.style.display = '';
          directionsEl.setAttribute('href', 'https://www.google.com/maps/dir/?api=1&destination='
            + encodeURIComponent(livePing.lat + ',' + livePing.lng));
          directionsEl.textContent = 'Get Directions';
        }
        updateProximity();
        return;
      }

      if (active) {
        currentTarget = active;
        root.classList.remove('wb-bar--closed');
        if (orderingOff) {
          root.classList.remove('wb-bar--live');
          root.classList.add('wb-bar--paused');
          if (statusEl) { statusEl.textContent = 'AT ' + active.name + ' \u2014 online orders paused'; }
        } else {
          root.classList.add('wb-bar--live');
          root.classList.remove('wb-bar--paused');
          if (statusEl) { statusEl.textContent = 'NOW SERVING \u2014 ' + active.name; }
        }
        if (subEl) { subEl.textContent = windowLabel(active) + (active.address ? ' \u00b7 ' + active.address : ''); }
        if (modalSpotEl) { modalSpotEl.textContent = active.name; }
        if (modalWindowEl) { modalWindowEl.textContent = 'Serving ' + windowLabel(active); }
        if (directionsEl) {
          directionsEl.style.display = '';
          directionsEl.setAttribute('href', directionsUrl(active));
          directionsEl.textContent = 'Get Directions';
        }
      } else {
        root.classList.remove('wb-bar--live');
        root.classList.add('wb-bar--closed');
        var next = findNext(windows, current);
        if (next) {
          currentTarget = next.slot;
          var mins = next.minutesAway;
          if (statusEl) { statusEl.textContent = 'NEXT STOP \u2014 ' + next.slot.name; }

          if (subEl) {
            if (mins <= 60) {
              /* Live ticking countdown inside the final hour. */
              var secsLeft = (mins * 60) - now.getSeconds();
              var mm = Math.floor(secsLeft / 60);
              var ss = secsLeft % 60;
              subEl.innerHTML = 'Opens in <span class="wb-bar__count">' + pad(mm) + ':' + pad(ss) + '</span>';
            } else {
              var label = DAY_SHORT[next.slot.day] + ' ' + formatClock(next.slot.startClock);
              var away;
              if (mins < 1440) {
                away = 'in ' + Math.floor(mins / 60) + 'h ' + pad(mins % 60) + 'm';
              } else {
                var days = Math.floor(mins / 1440);
                away = 'in ' + days + (days === 1 ? ' day' : ' days');
              }
              subEl.textContent = label + ' \u00b7 ' + away;
            }
          }

          if (modalSpotEl) { modalSpotEl.textContent = next.slot.name; }
          if (modalWindowEl) { modalWindowEl.textContent = DAY_NAMES[next.slot.day] + ', ' + windowLabel(next.slot); }
          if (directionsEl) {
            directionsEl.style.display = '';
            directionsEl.setAttribute('href', directionsUrl(next.slot));
            directionsEl.textContent = 'Directions';
          }
        }
      }

      /* Highlight the active row in the modal list. */
      if (listEl) {
        var rows = listEl.querySelectorAll('[data-wb-row]');
        for (var i = 0; i < rows.length; i++) {
          var key = active ? (active.name + '|' + active.day) : null;
          if (key && rows[i].getAttribute('data-wb-row') === key) { rows[i].classList.add('is-now'); }
          else { rows[i].classList.remove('is-now'); }
        }
      }

      updateProximity();
    }

    /* ------------------------------------------------------- proximity */

    function updateProximity() {
      if (!wantProximity || !nearEl || !visitorPos || !currentTarget) { return; }
      if (isNaN(currentTarget.lat) || isNaN(currentTarget.lng)) {
        nearEl.hidden = true;
        return;
      }
      var miles = haversineMiles(visitorPos.lat, visitorPos.lng, currentTarget.lat, currentTarget.lng);
      var shown = miles < 10 ? miles.toFixed(1) : Math.round(miles);
      nearEl.hidden = false;
      nearEl.textContent = '\u2299 ' + shown + ' mi away';
    }

    if (nearBtn) {
      nearBtn.addEventListener('click', function () {
        if (!navigator.geolocation) {
          nearBtn.textContent = 'Not supported';
          return;
        }
        nearBtn.textContent = 'Locating...';
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            visitorPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            nearBtn.style.display = 'none';
            updateProximity();
          },
          function () { nearBtn.textContent = 'Location off'; },
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
        );
      });
    }

    /* ----------------------------------------------------------- modal */

    var modal = root.querySelector('[data-wb-modal]');
    var lastFocus = null;

    function openModal() {
      if (!modal) { return; }
      lastFocus = document.activeElement;
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      var close = modal.querySelector('[data-wb-close]');
      if (close) { close.focus(); }
    }
    function closeModal() {
      if (!modal) { return; }
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) { lastFocus.focus(); }
    }

    var openers = root.querySelectorAll('[data-wb-open]');
    for (var o = 0; o < openers.length; o++) { openers[o].addEventListener('click', openModal); }
    var closer = root.querySelector('[data-wb-close]');
    if (closer) { closer.addEventListener('click', closeModal); }
    if (modal) {
      modal.addEventListener('click', function (e) { if (e.target === modal) { closeModal(); } });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) { closeModal(); }
    });

    /* Add to Calendar — delegated so it survives a feed rebuild. */
    if (listEl && wantCalendar) {
      listEl.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('[data-wb-ics]') : null;
        if (!btn) { return; }
        var idx = parseInt(btn.getAttribute('data-wb-ics'), 10);
        var match = null;
        for (var i = 0; i < windows.length; i++) {
          if (windows[i].index === idx) { match = windows[i]; break; }
        }
        if (!match) { match = windows[idx]; }
        if (match) { downloadIcs(match, shopName); }
      });
    }

    /* Pause animation while the tab is hidden so we do not burn battery. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { root.classList.add('is-hidden-tab'); }
      else { root.classList.remove('is-hidden-tab'); render(); }
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-wb-tracker]');
    for (var i = 0; i < roots.length; i++) { init(roots[i]); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('[data-wb-tracker]') : null;
    if (root) { root.removeAttribute('data-wb-ready'); init(root); }
  });
})();
